import * as React from 'react'
import TopNavigation from '../components/Navigation/TopNavigation'
import { AppState } from '../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ERoute from '../ERoute'
import { RouteComponentProps } from 'react-router'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import { showConfirmModal, showWidgetModal } from '../store/modals/actions'
import PageContent from '../components/Page/PageContent'
import PageHeader from '../components/Page/PageHeader'
import { Helmet } from 'react-helmet'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Widget, WidgetFilter, WidgetFilters, CurrentUser, ResourceListFilterType } from '../types'
import LocalStorage, { LocalStorageKey } from '../LocalStorage'
import ResourceTableRow from '../components/Resource/ResourceTableRow'
import ResourceTableRowData from '../components/Resource/ResourceTableRowData'
import ResourceTableRowActions from '../components/Resource/ResourceTableRowActions'
import CardEmptyInfo from '../components/Card/CardEmptyInfo'
import ResourceTable, { ResourceTableAction } from '../components/Resource/ResourceTable'
import PageLoader from '../components/Page/PageLoader'
import { WidgetsController } from '../controllers'
import RouteHelper from '../helpers/RouteHelper'
import Notification from '../utilities/Notification'
import Icon from '../components/Icons/Icon'
import SearchHelper from '../helpers/SearchHelper'
import NavigationDetection from '../components/Effects/NavigationDetection'
import Switch from '../components/Form/Switch'

interface IStateProps extends RouteComponentProps<{ groupId?: string }> {
	currentUser: CurrentUser
}

interface IDispatchToProps {
	showWidgetModal: typeof showWidgetModal
	showConfirmModal: typeof showConfirmModal
}

interface IState {
	widgets: Widget[],
	page: number,
	perPage: number,
	totalPages: number
	didInitialLoad: boolean
	isFetching: boolean
	sortValue: string
	activeFilters: any,
	selectedWidgetIds: string[]
	search: string
}

type IProps = IStateProps & IDispatchToProps & WithTranslation

class Widgets extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)

		const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, WidgetFilters)

		this.state = {
			widgets: [],
			page: page,
			perPage: perPage,
			totalPages: 0,
			didInitialLoad: false,
			isFetching: false,
			sortValue: LocalStorage.get(LocalStorageKey.WIDGET_SORT_VALUE, '-'),
			selectedWidgetIds: [],
			search: search,
			activeFilters: filters,
		}

		this.onRouteChange = this.onRouteChange.bind(this)
		this.onNewWidgetClick = this.onNewWidgetClick.bind(this);
		this.onTableWidgetClick = this.onTableWidgetClick.bind(this)
		this.onTableActionClick = this.onTableActionClick.bind(this)
		this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
		this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
		this.onTableWidgetDeleteClick = this.onTableWidgetDeleteClick.bind(this);
		this.onWidgetFormSubmit = this.onWidgetFormSubmit.bind(this)
		this.onWidgetUpdateFormSubmit = this.onWidgetUpdateFormSubmit.bind(this)
		this.onWidgetCreate = this.onWidgetCreate.bind(this)
		this.onWidgetFiltersChange = this.onWidgetFiltersChange.bind(this)
		this.onWidgetSortValueChange = this.onWidgetSortValueChange.bind(this)
		this.onWidgetSearchChange = this.onWidgetSearchChange.bind(this)
		this.onWidgetSearchSubmit = this.onWidgetSearchSubmit.bind(this)
		this.onWidgetClearFilters = this.onWidgetClearFilters.bind(this)
		this.onBulkDeleteClick = this.onBulkDeleteClick.bind(this)
		this.onPageChange = this.onPageChange.bind(this)
		this.onPerPageChange = this.onPerPageChange.bind(this)
		this.onToggleEnabledChange = this.onToggleEnabledChange.bind(this)
	}

	onRouteChange() {
		const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, WidgetFilters)

		this.setState({
			search: search,
			page: page,
			perPage: perPage,
			activeFilters: filters
		}, () => {
			this.fetchWidgets(page)
		})
	}

	async fetchWidgets(page: number) {
		const { search, sortValue, activeFilters, perPage } = this.state
		this.setState({
			isFetching: true
		}, async () => {
			try {
				const response = await WidgetsController.getWidgets({
					page: page,
					per_page: perPage,
					search: search,
					order: `${sortValue}`,
					...activeFilters,
				})
				const { widgets, current_page, total_pages, total_entries } = response;

				this.setState({
					widgets: [...widgets],
					page: current_page,
					totalPages: total_pages,
					didInitialLoad: true,
					isFetching: false
				});
			} catch (ex) {
				console.error(ex)
			}
		});
	}

	onNewWidgetClick(e: React.MouseEvent<HTMLAnchorElement>) {
		e.preventDefault()

		const { showWidgetModal } = this.props

		showWidgetModal({
			widget: {},
			onSubmit: this.onWidgetFormSubmit,
		})
	}

	onTableWidgetClick(widget: Widget) {
		this.props.history.push(RouteHelper.process(ERoute.PATH_WIDGET, { id: widget.id }))
	}

	onTableActionClick(key: string, widget: Widget) {
		switch (key) {
			case 'delete': this.onTableWidgetDeleteClick(widget)
				break
			default:
				throw Error('[Widgets] Unimplemented onTableActionClick')
		}
	}

	onTableSelectionChange(selectedWidgetIds: string[]) {
		this.setState({ selectedWidgetIds: selectedWidgetIds })
	}

	onTableRowSelectionChange(selected: boolean, transactionId: string) {
		const { selectedWidgetIds } = this.state

		if (selected) {
			this.setState({ selectedWidgetIds: [...selectedWidgetIds, transactionId] })
		} else {
			this.setState({ selectedWidgetIds: selectedWidgetIds.filter(selectedTransactionId => selectedTransactionId !== transactionId) })
		}
	}

	onTableWidgetDeleteClick(widget: Widget) {
		const { showConfirmModal, t } = this.props

		showConfirmModal({
			title: t('Widget::Delete widget'),
			description: t('Widget::You are about to delete {{name}}. By deleting this widget you are also deleting all its associated data. Are you sure?', { name: widget.name }),
			action: { label: t('Widget::Delete'), isDestructive: true },
			onConfirm: () => {
				WidgetsController
					.delete(widget.id)
					.then((response) => {
						if (response.errors) {

						} else {
							const { widgets } = this.state;

							const widgetIndex = widgets.findIndex(c => c.id === widget.id);

							widgets.splice(widgetIndex, 1);

							this.setState({
								widgets: widgets
							});

							Notification.notifySuccess(t('Widget::Widget successfully deleted'))
						}
					})
					.catch(console.error)
			}
		})
	}

	onWidgetFormSubmit(widget: Widget) {
		const { widgets } = this.state;

		const newWidgets = [widget, ...widgets];

		this.setState({ widgets: newWidgets });
	}

	onWidgetUpdateFormSubmit(widget: Widget) {
		const { widgets } = this.state

		const widgetIndex = widgets.findIndex(c => c.id === widget.id);

		if (widgetIndex !== -1) {
			widgets[widgetIndex] = widget
		}

		this.setState({
			widgets: [
				...widgets,
			]
		})
	}

	onWidgetCreate(data) {
		const { widgets } = this.state
		const { detail: { widget } } = data

		this.setState({
			widgets: [
				widget,
				...widgets,
			]
		})
	}

	onWidgetFiltersChange(activeFilters: any) {
		this.setState({ page: 1, activeFilters: activeFilters }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onWidgetSortValueChange(value: string) {
		LocalStorage.set(LocalStorageKey.HELP_CENTER_SORT_VALUE, value)
		this.setState({ page: 1, sortValue: value }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onWidgetSearchChange(search) {
		this.setState({ search: search })
	}

	onWidgetSearchSubmit(search) {
		this.setState({ page: 1, search: search }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onWidgetClearFilters() {
		this.setState({
			page: 1,
			search: '',
			activeFilters: {}
		}, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onBulkDeleteClick() {
		const { showConfirmModal, t } = this.props
		const { widgets, selectedWidgetIds } = this.state

		showConfirmModal({
			title: t('Widget::Delete widgets'),
			description: t('Widget::You are about to delete {{count}} widgets. By deleting these widgets you are also deleting all their associated data. Are you sure?', { count: selectedWidgetIds.length }),
			action: { label: t('Widget::Delete'), isDestructive: true },
			onConfirm: async () => {
				try {
					await WidgetsController.bulkDelete(selectedWidgetIds)

					this.setState({
						selectedWidgetIds: [],
						widgets: widgets.filter(widget => !selectedWidgetIds.includes(widget.id))
					})
				} catch (ex) {
					console.error(ex)
				}
			}
		})
	}

	onPageChange(page: number) {
		this.setState({ page: page }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onPerPageChange(perPage: number) {
		this.setState({ page: 1, perPage: perPage }, () => {
			const { page, perPage, activeFilters, search, sortValue } = this.state
			LocalStorage.set(LocalStorageKey.PER_PAGE, perPage)
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, perPage: perPage, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	async onToggleEnabledChange(e: any, widget: Widget) {
		e.preventDefault()
		e.stopPropagation()
		const { widgets } = this.state

		try {
			const enabled = !widget.enabled
			this.setState({ widgets: widgets.map(c => c.id === widget.id ? { ...c, enabled: enabled } : c) })
			await WidgetsController.update({ ...widget, enabled: enabled })
		} catch (ex) {
			console.error(ex)
		}
	}

	render() {
		const { currentUser, t } = this.props
		const { widgets, didInitialLoad, isFetching, activeFilters, sortValue, search, page, perPage, totalPages, selectedWidgetIds } = this.state
		const { workspace: { setting } } = currentUser

		const activeFiltersCount = search?.length > 0 || Object.keys(activeFilters).length > 0
		const promotedBulkActions: ResourceTableAction[] = [
			{ icon: 'trash-alt-solid', content: t('Widgets::Delete'), onAction: this.onBulkDeleteClick, destructive: true }
		]

		const bulkActions: ResourceTableAction[] = []
		const filters: WidgetFilter[] = [
			{ name: 'name', label: t('Widget::Name'), type: ResourceListFilterType.STRING },
			{ name: 'enabled', label: t('Widget::Enabled'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('Widgets::Yes'), value: String(true) }, { label: t('Widgets::No'), value: String(false) }] },
			{ name: 'created_at', label: t('Widget::Created date'), type: ResourceListFilterType.DATE },
		]

		return (
			<>
				<Helmet>
					<title>{t('Widgets::{{__appName}} | Widgets')}</title>
				</Helmet>
				<ScrollToTopOnMount />
				<NavigationDetection onRouteChange={this.onRouteChange} />
				<TopNavigation
					icon='book'
					title={t('Widgets::Widgets')}
					action={
						<a key='new-project' href='javascript://' className='button button-primary page-action' onClick={this.onNewWidgetClick}>
							<Icon icon='plus' />
							{t('Widgets::New widget')}
						</a>
					}
				/>

				<PageContent>
					<PageHeader
						title={t('Widgets::Widgets')}
					/>

					{!didInitialLoad && <PageLoader />}
					{didInitialLoad && <ResourceTable
						data={widgets}
						headers={[
							{ title: t('Widgets::Name') },
							{ title: t('Widgets::Enabled') },
							{ title: '', stickyRight: '0px' },
						]}
						renderRow={(widget: Widget) => {
							return (
								<ResourceTableRow
									key={widget.id}
									selected={selectedWidgetIds.includes(widget.id)}
									onSelectionChange={(selected) => this.onTableRowSelectionChange(selected, widget.id)}
								>
									<ResourceTableRowData onClick={() => this.onTableWidgetClick(widget)} maxWidth='500px' ellipse>
										{widget.name}
									</ResourceTableRowData>
									<ResourceTableRowData onClick={() => this.onTableWidgetClick(widget)} maxWidth='500px' ellipse>
										<Switch
											name='enabled'
											onClick={(e) => this.onToggleEnabledChange(e, widget)}
											checked={widget.enabled}
										/>
									</ResourceTableRowData>
									<ResourceTableRowActions
										actions={[
											{ key: 'update', icon: 'edit-solid', content: t('Widget::Edit') },
											{ key: 'delete', icon: 'trash-alt-solid', content: t('Widget::Delete'), destructive: true }
										]}
										onActionClick={(key) => this.onTableActionClick(key, widget)}
										sticky={true}
										stickyRight='0px'
									/>
								</ResourceTableRow>
							)
						}}
						renderEmpty={<CardEmptyInfo
							icon={activeFiltersCount ? 'search' : 'messenger-home-active'}
							description={activeFiltersCount ? t('Widget::No widgets found') : t('Widget::No widgets have been created yet')}
							descriptionActionText={activeFiltersCount ? t('Widget::Clear filters') : t('Widget::Add new widget')}
							onDescriptionActionClick={activeFiltersCount ? this.onWidgetClearFilters : this.onNewWidgetClick}
						/>}
						filters={filters}
						activeFilters={activeFilters}
						onFiltersChange={this.onWidgetFiltersChange}
						sortOptions={[
							{ label: '-', value: '-' },
							{ label: t('Widget::Name (A-Z)'), value: 'name_asc' },
							{ label: t('Widget::Name (Z-A)'), value: 'name_desc' },
							{ label: t('Widget::Created at ↑'), value: 'created_at_asc' },
							{ label: t('Widget::Created at ↓'), value: 'created_at_desc' },
						]}
						promotedBulkActions={promotedBulkActions}
						bulkActions={bulkActions}
						sortValue={sortValue}
						onSortChange={this.onWidgetSortValueChange}
						pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
						onPageChange={this.onPageChange}
						onPerPageChange={this.onPerPageChange}
						isLoading={isFetching}
						stickyHeader={true}
						selectedItems={selectedWidgetIds}
						onSelectionChange={this.onTableSelectionChange}
						searchValue={search}
						onSearchChange={this.onWidgetSearchChange}
						onSearchSubmit={this.onWidgetSearchSubmit}
						maxHeight='60vh'
					/>}
				</PageContent>
			</>
		)
	}
}

interface IStateToProps {
	currentUser: CurrentUser
}

const mapStateToProps = (state: AppState): IStateToProps => {
	const {
		authentication: {
			currentUser,
		}
	} = state

	return {
		currentUser: currentUser,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
	return {
		showWidgetModal: (options) => dispatch(showWidgetModal(options)),
		showConfirmModal: (options) => dispatch(showConfirmModal(options)),
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Widgets))